import React, { useEffect, useState, useRef } from 'react';
import { Space, Button, Alert, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import api from '../../../services/api';

const { Text, Title } = Typography;
const POLL_MS = 3000;
const MAX_WAIT_MS = 5 * 60 * 1000; // 5 min

// Painel de pagamento Pix: mostra QR code + copia-cola, conta o tempo e
// fica perguntando pro backend se ja caiu o pagamento.
// props: { purchase: {purchase_id, qr_code, qr_code_base64, expires_at}, teamId, onPaid, onExpired }
export default function PixPaymentPanel({ purchase, teamId, onPaid, onExpired }) {
  const [status, setStatus] = useState('pending');
  const [remaining, setRemaining] = useState(Math.round(MAX_WAIT_MS / 1000));
  const startRef = useRef(Date.now());
  const timeoutRef = useRef(null);

  useEffect(() => {
    let stopped = false;

    const tick = async () => {
      if (stopped) return;
      const elapsed = Date.now() - startRef.current;
      setRemaining(Math.max(0, Math.round((MAX_WAIT_MS - elapsed) / 1000)));

      if (elapsed > MAX_WAIT_MS) {
        setStatus('expired');
        onExpired?.();
        return;
      }

      try {
        const r = await api.get(
          `/community/finance/credits/purchase/${purchase.purchase_id}/status`,
          { params: { team_id: teamId } }
        );
        if (stopped) return;
        if (r.data.status === 'paid') {
          setStatus('paid');
          message.success('Pagamento confirmado! Os créditos já caíram no seu saldo.');
          onPaid?.(r.data);
          return;
        }
        if (r.data.status === 'failed') {
          setStatus('failed');
          return;
        }
        if (r.data.status === 'expired') {
          setStatus('expired');
          onExpired?.();
          return;
        }
        // pending: continua no jogo
      } catch (e) {
        const authStatus = e.response?.status;
        if (authStatus === 401 || authStatus === 403) {
          // sessao expirou/perdeu permissao: parar de bater no endpoint pra
          // sempre, senao fica em loop infinito sem chance de confirmar nada
          setStatus('failed');
          return;
        }
        // outros erros (rede, 5xx): silencioso, tenta de novo no proximo tick
      }

      if (!stopped) {
        timeoutRef.current = setTimeout(tick, POLL_MS);
      }
    };

    timeoutRef.current = setTimeout(tick, POLL_MS);

    return () => {
      stopped = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchase.purchase_id, teamId]);

  const copyCode = async () => {
    const code = purchase.qr_code || '';
    if (!code) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(code);
        message.success('Código Pix copiado, bora colar lá no banco');
        return;
      } catch (e) {
        // cai pro fallback abaixo
      }
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) {
        message.success('Código Pix copiado, bora colar lá no banco');
      } else {
        message.info('Não rolou copiar automático, seleciona o código aí embaixo e copia na mão');
      }
    } catch (e) {
      message.info('Não rolou copiar automático, seleciona o código aí embaixo e copia na mão');
    }
  };

  if (status === 'paid') {
    return (
      <Alert
        type="success"
        showIcon
        message="Pagamento confirmado!"
        description="Os créditos já estão disponíveis no seu saldo."
      />
    );
  }
  if (status === 'expired') {
    return (
      <Alert
        type="warning"
        showIcon
        message="O Pix expirou"
        description="Sem problema, gera um novo Pix pra continuar."
      />
    );
  }
  if (status === 'failed') {
    return (
      <Alert
        type="error"
        showIcon
        message="Pagamento não passou"
        description="Tenta de novo ou usa outro método de pagamento."
      />
    );
  }

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return (
    <Space direction="vertical" style={{ width: '100%', alignItems: 'center' }} size="middle">
      <Title level={5} style={{ margin: 0, textAlign: 'center' }}>
        Escaneia o QR Code no app do teu banco
      </Title>
      {purchase.qr_code_base64 && (
        <img
          src={`data:image/png;base64,${purchase.qr_code_base64}`}
          alt="QR Code Pix"
          width={240}
          height={240}
          style={{ border: '1px solid #eee', borderRadius: 8 }}
        />
      )}
      <Text type="secondary">Ou copia o código Pix:</Text>
      <div
        style={{
          background: '#f5f5f5',
          padding: 12,
          borderRadius: 8,
          wordBreak: 'break-all',
          fontFamily: 'monospace',
          fontSize: 11,
          maxWidth: '100%',
        }}
      >
        {purchase.qr_code}
      </div>
      <Button icon={<CopyOutlined />} onClick={copyCode} type="primary" block>
        Copiar código Pix
      </Button>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Aguardando pagamento... {mins > 0 ? `${mins}m ` : ''}{secs}s pra expirar
      </Text>
    </Space>
  );
}
